import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Auth events admin view — reads from public.events filtered to AUTH context.
 *
 * Source of truth: src/core/auth/auth.tsx writes via logAuthEvent() with
 *   context = { page: 'AUTH', auth_event_type: <specific type> }
 *   event_name = AUTH_LOGIN | AUTH_LOGOUT | ERROR
 *
 * This is read-only — RLS already permits admin role to SELECT all rows.
 */

export interface AuthEvent {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  event_name: string;             // AUTH_LOGIN | AUTH_LOGOUT | ERROR
  auth_event_type: string;        // specific subtype from context.auth_event_type
  session_id: string | null;
  payload: Record<string, unknown> | null;
  context: Record<string, unknown> | null;
  created_at: string;
}

interface UseAdminAuthEventsResult {
  events: AuthEvent[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  fetchEvents: (filters?: AuthEventFilters, page?: number) => Promise<void>;
  exportToCsv: () => void;
}

export interface AuthEventFilters {
  authEventType?: string;       // specific subtype filter (AUTH_LOGIN, BOOTSTRAP_ERROR, etc.)
  userIdOrEmail?: string;       // free text search — matches user id OR email substring
  startDate?: string;           // ISO date — created_at >= start
  endDate?: string;             // ISO date — created_at <= end
}

export function useAdminAuthEvents(): UseAdminAuthEventsResult {
  const [events, setEvents] = useState<AuthEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const fetchEvents = useCallback(async (
    filters: AuthEventFilters = {},
    pageNum: number = 1
  ) => {
    setLoading(true);
    try {
      // If user filter looks like an email, resolve to user ids first
      let userIdFilter: string[] | null = null;
      if (filters.userIdOrEmail && filters.userIdOrEmail.includes('@')) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, email')
          .ilike('email', `%${filters.userIdOrEmail}%`)
          .limit(50);
        userIdFilter = (profilesData ?? []).map(p => p.id);
        if (userIdFilter.length === 0) {
          // No matching users — empty result, save the round-trip
          setEvents([]);
          setTotal(0);
          setPage(pageNum);
          return;
        }
      } else if (filters.userIdOrEmail) {
        // Treat as a partial UUID
        userIdFilter = [filters.userIdOrEmail];
      }

      let query = supabase
        .from('events')
        .select('id, user_id, session_id, event_name, context, payload, created_at', { count: 'exact' })
        .filter('context->>page', 'eq', 'AUTH')
        .order('created_at', { ascending: false })
        .range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

      if (filters.authEventType) {
        query = query.filter('context->>auth_event_type', 'eq', filters.authEventType);
      }
      if (userIdFilter && userIdFilter.length === 1) {
        query = query.eq('user_id', userIdFilter[0]);
      } else if (userIdFilter && userIdFilter.length > 1) {
        query = query.in('user_id', userIdFilter);
      }
      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      const { data: eventsData, error: eventsError, count } = await query;

      if (eventsError) {
        // Table missing or RLS denied — fail soft
        const isTableMissing =
          eventsError.code === '42P01' ||
          eventsError.message?.includes('does not exist');
        if (isTableMissing) {
          setEvents([]);
          setTotal(0);
          setPage(pageNum);
          return;
        }
        throw eventsError;
      }

      // Resolve user names for display
      const userIds = [...new Set((eventsData ?? []).map(e => e.user_id).filter(Boolean))];
      let profilesMap: Record<string, { name: string | null; email: string | null }> = {};
      if (userIds.length > 0) {
        try {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, full_name, email')
            .in('id', userIds);
          if (profilesData) {
            profilesMap = profilesData.reduce((acc, p) => {
              acc[p.id] = {
                name: (p as { full_name?: string | null }).full_name ?? null,
                email: (p as { email?: string | null }).email ?? null,
              };
              return acc;
            }, {} as Record<string, { name: string | null; email: string | null }>);
          }
        } catch {
          // Silent — names are nice-to-have, not required
        }
      }

      const mapped: AuthEvent[] = (eventsData ?? []).map(e => {
        const ctx = (e.context ?? {}) as Record<string, unknown>;
        const profile = profilesMap[e.user_id] ?? { name: null, email: null };
        return {
          id: e.id,
          user_id: e.user_id,
          user_name: profile.name,
          user_email: profile.email,
          event_name: e.event_name,
          auth_event_type: typeof ctx.auth_event_type === 'string' ? ctx.auth_event_type : e.event_name,
          session_id: e.session_id,
          payload: e.payload as Record<string, unknown> | null,
          context: ctx,
          created_at: e.created_at,
        };
      });

      setEvents(mapped);
      setTotal(count ?? 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch auth events:', err);
      toast.error('Failed to fetch auth events');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const exportToCsv = useCallback(() => {
    if (events.length === 0) {
      toast.error('No events to export');
      return;
    }

    const headers = ['Timestamp', 'User Email', 'User ID', 'Event Type', 'Subtype', 'Session ID', 'Payload'];
    const rows = events.map(e => [
      new Date(e.created_at).toISOString(),
      e.user_email ?? '',
      e.user_id,
      e.event_name,
      e.auth_event_type,
      e.session_id ?? '',
      e.payload ? JSON.stringify(e.payload) : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `auth-events-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('Auth events exported');
  }, [events]);

  return {
    events,
    total,
    page,
    pageSize,
    loading,
    fetchEvents,
    exportToCsv,
  };
}

/** Common AUTH event subtypes, for the filter dropdown. */
export const AUTH_EVENT_TYPES: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All Auth Events' },
  { value: 'AUTH_LOGIN', label: 'Login' },
  { value: 'AUTH_LOGOUT', label: 'Logout' },
  { value: 'AUTH_BOOT_START', label: 'Boot — Start' },
  { value: 'AUTH_BOOT_SESSION_PRESENT', label: 'Boot — Session Restored' },
  { value: 'AUTH_BOOT_NO_SESSION', label: 'Boot — No Session' },
  { value: 'AUTH_BOOT_TIMEOUT', label: 'Boot — Timeout' },
  { value: 'AUTH_CALLBACK_START', label: 'OAuth Callback — Start' },
  { value: 'AUTH_CALLBACK_EXCHANGED', label: 'OAuth Callback — Exchanged' },
  { value: 'AUTH_CALLBACK_FAILED', label: 'OAuth Callback — Failed' },
  { value: 'AUTH_STATE_CHANGE', label: 'State Change' },
  { value: 'AUTH_LOOP_DETECTED', label: 'Loop Detected' },
  { value: 'GATE_CHECK_START', label: 'Gate — Check Start' },
  { value: 'GATE_RESULT', label: 'Gate — Result' },
  { value: 'BOOTSTRAP_ERROR', label: 'Bootstrap — Error' },
];
